import {
	BarChart3,
	CreditCard,
	FileText,
	Key,
	LayoutDashboard,
	Package,
	Settings,
	Shield,
	TestTube,
	Users,
	X,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { cn } from "@/lib/utils";

interface SidebarProps {
	onClose?: () => void;
}

const navigation = [
	{
		name: "Dashboard",
		href: "/dashboard",
		icon: LayoutDashboard,
		description: "Overview and analytics",
	},
	{
		name: "Packages",
		href: "/dashboard/packages",
		icon: Package,
		description: "Manage your packages",
	},
	{
		name: "Analytics",
		href: "/dashboard/analytics",
		icon: BarChart3,
		description: "Usage and performance",
	},
	{
		name: "Team",
		href: "/dashboard/team",
		icon: Users,
		description: "Team members and roles",
	},
	{
		name: "API Tokens",
		href: "/dashboard/tokens",
		icon: Key,
		description: "Authentication tokens",
	},
	{
		name: "Policies",
		href: "/dashboard/policies",
		icon: Shield,
		description: "Access and security policies",
	},
	{
		name: "Billing",
		href: "/dashboard/billing",
		icon: CreditCard,
		description: "Plans and invoices",
	},
	{
		name: "Settings",
		href: "/dashboard/settings",
		icon: Settings,
		description: "Tenant configuration",
	},
	{
		name: "Audit Logs",
		href: "/dashboard/logs",
		icon: FileText,
		description: "Security and activity logs",
	},
	{
		name: "API Test",
		href: "/dashboard/api-test",
		icon: TestTube,
		description: "Test API integration",
	},
];

export function Sidebar({ onClose }: SidebarProps) {
	const location = useLocation();
	const { tenant } = useTenant();

	return (
		<div className="h-full w-64 bg-card border-r border-border flex flex-col">
			<div className="flex flex-col h-full">
				{/* Logo and tenant */}
				<div className="flex items-center justify-between px-6 py-5 border-b border-border">
					<div className="flex items-center space-x-3">
						<div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
							<Package className="w-5 h-5 text-white" />
						</div>
						<div className="flex flex-col">
							<span className="text-sm font-semibold text-foreground">
								{tenant?.name || "Artify"}
							</span>
							<span className="text-xs text-muted-foreground capitalize">
								{tenant?.subscription?.plan || "Free"} Plan
							</span>
						</div>
					</div>

					{/* Close button for mobile */}
					{onClose && (
						<button
							onClick={onClose}
							className="lg:hidden p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
						>
							<X className="w-5 h-5" />
						</button>
					)}
				</div>

				{/* Navigation */}
				<nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
					{navigation.map((item) => {
						const isActive =
							location.pathname === item.href ||
							(item.href !== "/dashboard" &&
								location.pathname.startsWith(item.href));

						return (
							<Link
								key={item.name}
								to={item.href}
								className={cn(
									"group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
									isActive
										? "bg-primary text-primary-foreground"
										: "text-foreground hover:bg-accent hover:text-accent-foreground",
								)}
							>
								<item.icon
									className={cn(
										"flex-shrink-0 w-4 h-4 mr-3",
										isActive
											? "text-primary-foreground"
											: "text-muted-foreground group-hover:text-accent-foreground",
									)}
								/>
								<div className="flex flex-col flex-1">
									<span className="font-semibold">{item.name}</span>
									<span
										className={cn(
											"text-xs mt-0.5",
											isActive
												? "text-primary-foreground/90"
												: "text-muted-foreground group-hover:text-accent-foreground/80",
										)}
									>
										{item.description}
									</span>
								</div>
							</Link>
						);
					})}
				</nav>

				{/* Footer */}
				<div className="px-4 py-4 border-t border-border">
					<div className="text-xs text-muted-foreground text-center">
						Artify Registry v0.1.0
					</div>
				</div>
			</div>
		</div>
	);
}
